<template>
    <div class="col-sm-12 editor-form">
        <h2 class="editor-header">{{ $t('contracts.update_contract_prices') }}</h2>
        <transition name="slide-fade">
            <div v-if="orderOptions && orderOptions.length > 0" class="info-box">
                <font-awesome-icon class="info-circle" icon="info-circle" />
                <span>{{ $t("contracts.imported_contract_order_data_fetch_editor_decription") }}</span>
            </div>
        </transition>
        <div class="row mt-2" v-if="orderOptions && orderOptions.length > 0">
            <vue3-datatable
                ref="resultTable"
                :rows="orderOptions"
                :columns="fields"
                :pagination="false"
                :sticky-header="true"
                :has-checkbox="true"
                :height="orderOptions.length >7 ? '65vh' : '100%'"
                :sortable="true"
                skin="bh-table-compact"
                row-class="routa-table-row"
                cell-class="routa-table-cell"
                @row-select="rowSelected"
            >
                <template #order="row">
                    {{ row.value.order_id + " - " + row.value.order_name}}
                </template>
                <template #current_unit_price="row">
                    {{ row.value.current_unit_price ? (row.value.current_unit_price / 100).toFixed(3) : null }}
                </template>
                <template #current_pricing_model="row">
                    {{ pricingModelToText(row.value.current_pricing_model) }}
                </template>
                <template #contract_unit_price="row">
                    <span :class="{'text-danger': row.value.current_unit_price / 100 !== row.value.contract_unit_price / 100}">
                        {{ row.value.contract_unit_price ? (row.value.contract_unit_price / 100).toFixed(3) : null }}
                    </span>
                </template>
                <template #contract_pricing_model="row">
                    <span :class="{'text-danger': (row.value.current_pricing_model && row.value.current_pricing_model.id ? row.value.current_pricing_model.id : '') !== (row.value.contract_pricing_model && row.value.contract_pricing_model.id ? row.value.contract_pricing_model.id : '')}">
                        {{ pricingModelToText(row.value.contract_pricing_model) }}
                    </span>
                </template>
            </vue3-datatable>
        </div>

        <div class="row" v-if="!orderOptions || orderOptions.length === 0">
                <em>{{ $t('contracts.imported_order_data_not_found') }}</em>
        </div>
        <div class="col-sm-12 text-end my-2">
            <button
                type="submit"
                class="btn btn-secondary form-button"
                @click.stop="$emit('closeModal')"
            >
                {{ $t('common.cancel') }}
            </button>
            <button
                type="submit"
                class="btn btn-primary btn-success form-button"
                :disabled="selectedUpdateOrders.length === 0"
                @click.stop="saveOrderData"
            >
                {{ $t('common.update') }}
            </button>
        </div>
        <div
            v-if="loading > 0"
            id="loader"
            class="spinner"
        />
    </div>
</template>

<script>
import {restApi} from '@/components/mixins/RestApiMixin'
import {timeUtils} from '@/components/mixins/TimeUtils'
import {priceHelper} from "@/components/mixins/PricingMixin";
import Vue3Datatable from "@bhplugin/vue3-datatable";
import {inject} from "vue";
import {FontAwesomeIcon} from "@fortawesome/vue-fontawesome";

export default {
    name: "ContractDataPriceUpdater",
    components: {FontAwesomeIcon, Vue3Datatable},
    mixins: [restApi, timeUtils, priceHelper],
    props: {
        contractObject: {
            type: Object,
            default: null
        },
    },
    setup() {
        // Inject parents alerter
        const showAlert = inject('showAlert');
        return { showAlert };
    },
    data: function () {
        return {
            loading: 0,
            contract: null,
            importedContractData: [],
            selectedUpdateOrders: [],
            orderOptions: [],
            fields: [
                { field: 'order', title: this.$t('orders.order'), sort: true, headerClass: 'routa-table-header' },
                { field: 'current_unit_price', title: this.$t('contracts.current_price') + " (" + import.meta.env.VITE_APP_CURRENCY + ")", sort: true, headerClass: 'routa-table-header' },
                { field: 'current_pricing_model', title: this.$t('contracts.current_pricing_model'), sort: true, headerClass: 'routa-table-header' },
                { field: 'contract_unit_price', title: this.$t('contracts.contract_price') + " (" + import.meta.env.VITE_APP_CURRENCY + ")", sort: true, headerClass: 'routa-table-header' },
                { field: 'contract_pricing_model', title: this.$t('contracts.contract_pricing_model'), sort: true, headerClass: 'routa-table-header' },
            ]
        }
    },
    mounted() {
        this.fetchImportedContractOrderData()
    },
    methods: {
        toggleSelection(order) {
            const existingIndex = this.selectedUpdateOrders.findIndex(
                (selected) => selected.order_id === order.order_id
            );

            if (existingIndex !== -1) {
                // If an order with the same order_id exists, remove it
                this.selectedUpdateOrders.splice(existingIndex, 1);
            } else {
                // Otherwise, add this order
                this.selectedUpdateOrders.push(order);
            }
        },
        isSelected(order) {
            // Check if this exact order is in the selected list
            return this.selectedUpdateOrders.some((selected) => selected.order_id === order.order_id && selected === order);
        },
        isDisabled(order) {
            // If an order with the same order_id is already selected, disable other rows with the same order_id
            return this.selectedUpdateOrders.some((selected) => selected.order_id === order.order_id && selected !== order);
        },
        fetchImportedContractOrderData() {
            this.loading++
            this.restFetchParams(this.contractImportOrderPricesUrl, {contract: this.contractObject.id, contractNumber: this.contractObject.contract_number}, this.handleResponse, this.fail)
        },
        handleResponse(response) {
            this.loading--
            if(response.data) {
                this.importedContractData = response.data
                this.initOrderOptions()
            }
        },
        initOrderOptions() {
            this.orderOptions = []
            let temp = []
             if(this.importedContractData.orders) {
                 this.importedContractData.orders.forEach(order => temp.push(order))
             }
             this.$nextTick(() => this.orderOptions = temp)
        },
        saveOrderData() {
            this.loading++
            let json = this.setValues()
            this.restUpdate(this.contractImportOrderPricesUrl, json , this.handleSaveContractOrderData, this.handleSaveContractOrderDataFail)
        },
        handleSaveContractOrderData() {
            this.loading--
            this.showAlert(this.$t('common.alert_update_success'), 'success')
            this.$emit('closeSuccessModal')
        },
        setValues() {
            let contractChanges = {}
            if(this.importedContractData.contract_number) {
                contractChanges.contract_number = this.importedContractData.contract_number
            }
            if(this.contractObject.id) {
                contractChanges.contract = this.contractObject.id
            }
            if(this.selectedUpdateOrders && this.selectedUpdateOrders.length > 0) {
                contractChanges.order_price_updates = this.selectedUpdateOrders
            }
            return contractChanges
        },
        handleSaveContractOrderDataFail() {
            this.loading--
            this.showAlert(this.$t('contracts.imported_contract_data_update_failed'))
        },
        fail: function () {
            this.loading--
            this.showAlert(this.$t('contracts.imported_contract_data_fetching_failed'))
        },
        rowSelected(selection) {
            this.selectedUpdateOrders = selection
        }
    }
}
</script>
